import { useEffect, useCallback, useRef, useReducer, useMemo } from 'react';
import { downloadTradesAsCsv } from '../services/csvDownloadService';
import { GCLOUD_CONFIG } from '../config';
import { useAuth } from '../context/AuthContext';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { logger, correlationMiddleware } from '../utils/logger';

const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffFactor: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

const backtestReducer = (state, action) => {
  switch (action.type) {
    case 'RESET':
      return { ...state, loading: false, error: '', results: null, csvFilePath: '', jobStatus: 'idle', jobId: null, progress: 0 };
    case 'AUTH_CHECKING':
      return { ...state, loading: true, authChecking: true };
    case 'AUTH_READY':
      return { ...state, loading: false, authChecking: false };
    case 'START_JOB':
      return { ...state, loading: true, error: '', results: null, csvFilePath: '', jobStatus: 'queued', progress: 0, jobId: action.payload.jobId };
    case 'JOB_UPDATE':
      return { ...state, jobStatus: action.payload.status, progress: action.payload.progress || state.progress, ...(action.payload.results && { results: action.payload.results }) };
    case 'JOB_COMPLETED':
      return { ...state, loading: false, jobStatus: 'completed', results: action.payload.results || {} };
    case 'JOB_FAILED':
      return { ...state, loading: false, jobStatus: 'failed', error: action.payload.error || 'Job failed' };
    case 'SET_ERROR':
      return { ...state, loading: false, authChecking: false, error: action.payload.error, jobStatus: action.payload.jobStatus || state.jobStatus };
    case 'CANCEL_JOB':
      return { ...state, loading: false, jobStatus: 'idle' };
    default:
      return state;
  }
};

const initialState = {
  loading: false,
  error: '',
  results: null,
  csvFilePath: '',
  jobStatus: 'idle',
  jobId: null,
  progress: 0,
  authChecking: false,
};

const formatUserErrorMessage = (err) => {
  const msg = err.message || '';
  if (err.name === 'AbortError') return 'Request cancelled.';
  if (msg.includes('NetworkError') || msg.includes('Failed to fetch')) return 'Network error.';
  if (msg.includes('401') || msg.includes('not authenticated')) return 'Session expired.';
  return msg || 'Failed to start backtest job';
};

export const useBacktestApi = () => {
  const [state, dispatch] = useReducer(backtestReducer, initialState);
  const { loading, error, results, csvFilePath, jobStatus, jobId, progress, authChecking } = state;
  const { token, forceTokenRefresh, isTokenValid, user } = useAuth();

  const statusRef = useRef(jobStatus);
  useEffect(() => {
    statusRef.current = jobStatus;
  }, [jobStatus]);

  const eventSourceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const cleanupResources = useCallback(() => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    if (eventSourceRef.current) eventSourceRef.current.close();
    if (abortControllerRef.current) abortControllerRef.current.abort();
    retryCountRef.current = 0;
  }, []);

  const validateAuth = useCallback(async (forceRefresh = false) => {
    if (!mountedRef.current || !user || !token) throw new Error('Auth required');
    if (!isTokenValid() || forceRefresh) {
      const newToken = await forceTokenRefresh();
      if (!newToken) throw new Error('Token refresh failed');
      return newToken;
    }
    return token;
  }, [token, user?.uid, isTokenValid, forceTokenRefresh]);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const effectiveToken = await validateAuth();

    const attemptFetch = async (attempt = 0, forceToken = false) => {
      const currentToken = forceToken ? await validateAuth(true) : effectiveToken;
      
      const response = await fetch(url, {
        ...options,
        headers: { 
          'Content-Type': 'application/json', 
          ...options.headers, 
          'Authorization': `Bearer ${currentToken}` 
        },
      });

      if (response.status === 401 && attempt === 0) {
        return attemptFetch(attempt + 1, true);
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown Error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return response;
    };

    return attemptFetch();
  }, [validateAuth]);

  const setupEventSource = useCallback(async (jobId, cid) => {
    try {
      const currentToken = await validateAuth();
      cleanupResources();
      const newEventSource = new EventSourcePolyfill(`${GCLOUD_CONFIG.appApiUrl}/jobs/${jobId}/events`, {
        headers: { 'Authorization': `Bearer ${currentToken}`, 'X-Correlation-ID': cid },
        heartbeatTimeout: 300000,
      });
      eventSourceRef.current = newEventSource;

      newEventSource.addEventListener('message', (event) => {
        if (!mountedRef.current) return;
        const update = JSON.parse(event.data);
        dispatch({ type: 'JOB_UPDATE', payload: update });
        if (update.status === 'completed') {
          dispatch({ type: 'JOB_COMPLETED', payload: { results: update.results } });
          cleanupResources();
        }
      });

      newEventSource.addEventListener('close', () => {
        if (mountedRef.current && !['completed', 'failed'].includes(statusRef.current)) {
          dispatch({ type: 'SET_ERROR', payload: { error: 'Stream closed unexpectedly' } });
          cleanupResources();
        }
      });
    } catch (authError) {
      dispatch({ type: 'SET_ERROR', payload: { error: authError.message } });
    }
  }, [validateAuth, cleanupResources]);

  const getBacktestDetails = useCallback(async (runId) => {
    await validateAuth();
    const response = await fetchWithAuth(`${GCLOUD_CONFIG.appApiUrl}/backtest/runs/${runId}`);
    return response.json();
  }, [fetchWithAuth, validateAuth]);

  const runBacktest = useCallback(async (params) => {
    const { options: correlationOptions, cid } = correlationMiddleware();
    try {
      cleanupResources();
      dispatch({ type: 'RESET' });
      dispatch({ type: 'AUTH_CHECKING' });
      await validateAuth();
      dispatch({ type: 'AUTH_READY' });
      const response = await fetchWithAuth(`${GCLOUD_CONFIG.appApiUrl}/backtest/async`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: correlationOptions.headers,
      });
      const data = await response.json();
      if (data.jobId) {
        dispatch({ type: 'START_JOB', payload: { jobId: data.jobId } });
        await setupEventSource(data.jobId, cid);
      }
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: { error: formatUserErrorMessage(err), jobStatus: 'failed' } });
    }
  }, [validateAuth, fetchWithAuth, setupEventSource, cleanupResources]);

  const getBacktestHistory = useCallback(async () => {
    try {
      await validateAuth();
      const response = await fetchWithAuth(`${GCLOUD_CONFIG.appApiUrl}/backtest/history`);
      return response.json();
    } catch (err) { 
      return { runs: [], error: err.message }; 
    }
  }, [validateAuth, fetchWithAuth]);

  const cancelJob = useCallback(() => {
    cleanupResources();
    dispatch({ type: 'CANCEL_JOB' });
  }, [cleanupResources]);

  const downloadAndReset = useCallback(async () => {
    if (!results?.run_id) {
      dispatch({ type: 'SET_ERROR', payload: { error: 'No run ID found' } });
      return;
    }

    try {
      const runDetails = await getBacktestDetails(results.run_id);
      
      if (!runDetails?.trades?.length) {
        throw new Error('No trade data found in results');
      }

      const result = downloadTradesAsCsv(
        runDetails.trades, 
        results.run_name || 'backtest_results'
      );

      if (result.success) {
        dispatch({ type: 'RESET' });
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { error: `Download failed: ${err.message}` } 
      });
    }
  }, [results, getBacktestDetails]);

  const apiActions = useMemo(() => ({
    runBacktest,
    getBacktestHistory,
    getBacktestDetails,
    cancelJob,
    downloadAndReset,
    reset: () => dispatch({ type: 'RESET' }),
    refreshAuth: () => validateAuth(true),
  }), [runBacktest, getBacktestHistory, getBacktestDetails, cancelJob, downloadAndReset, validateAuth]);

  return {
    loading: loading || authChecking,
    error,
    results,
    csvFilePath,
    jobStatus,
    jobId,
    progress,
    authChecking,
    ...apiActions,
    isRunning: ['queued', 'processing'].includes(jobStatus),
    isCompleted: jobStatus === 'completed',
    canDownload: !!results?.run_id && jobStatus === 'completed',
    isAuthenticated: !!token && isTokenValid(),
  };
};